import { ChangeDetectionStrategy, Component, HostBinding, Input } from '@angular/core';
import { ICONS, IconDef } from '../../data/icon/icon.data';

// fa-* size tokens -> em. Mirrors Font Awesome's sizing scale.
const SIZE_EM: { [ key: string ]: string } = {
  xs: '0.75em', sm: '0.875em', lg: '1.333em', xl: '1.5em',
  '2x': '2em', '3x': '3em', '4x': '4em', '5x': '5em',
};

// fa-* tokens that are modifiers, never the icon name itself.
const MODIFIERS = new Set( [
  'fw', 'xs', 'sm', 'lg', 'xl', '1x', '2x', '3x', '4x', '5x', '6x', '7x', '8x',
  '9x', '10x', 'spin', 'pulse', 'beat', 'fade', 'bounce', 'border', 'inverse',
  'li', 'flip', 'flip-horizontal', 'flip-vertical', 'rotate-90', 'rotate-180',
  'rotate-270', 'pull-left', 'pull-right', 'stack', 'stack-1x', 'stack-2x',
] );

/**
 * Inline-SVG icon, the internal replacement for Font Awesome.
 *
 * `name` accepts either a bare icon name ("home", "github") or a full FA class
 * string ("fas fa-home", "fab fa-github", "fas fa-info fa-lg fa-fw") so data
 * that still carries FA classes (feature.icon, brand cssClass, …) keeps working
 * without edits. Renders the matching glyph from the generated ICONS registry
 * with `fill: currentColor`, so it inherits text colour exactly like the font.
 */
@Component( {
  selector: 'app-icon',
  templateUrl: './icon.component.html',
  styleUrls: [ './icon.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
} )
export class IconComponent {
  @Input() set name( value: string | undefined ) {
    this.resolve( value || '' );
  }

  /** Reserve a fixed 1.25em width and centre the glyph, like fa-fw. */
  @Input()
  @HostBinding( 'class.icon-fw' )
  fixedWidth = false;

  /** Optional size token: xs | sm | lg | xl | 2x … 5x. */
  @Input() set size( value: string | undefined ) {
    this._fontSize = value ? ( SIZE_EM[ value ] || null ) : null;
  }

  @HostBinding( 'style.font-size' )
  private _fontSize: string | null = null;

  def: IconDef | null = null;
  paths: string[] = [];

  private resolve( raw: string ): void {
    const tokens = raw.trim().split( /\s+/ ).filter( Boolean );
    const isBrand = tokens.indexOf( 'fab' ) !== -1;

    if ( tokens.indexOf( 'fa-fw' ) !== -1 ) {
      this.fixedWidth = true;
    }
    for ( const t of tokens ) {
      if ( t.indexOf( 'fa-' ) === 0 && SIZE_EM[ t.slice( 3 ) ] ) {
        this._fontSize = SIZE_EM[ t.slice( 3 ) ];
      }
    }

    let nm = '';
    const iconToken = tokens.find(
      t => t.indexOf( 'fa-' ) === 0 && !MODIFIERS.has( t.slice( 3 ) )
    );
    if ( iconToken ) {
      nm = iconToken.slice( 3 );
    } else {
      // bare name: first token that resolves in the registry, else first token.
      nm = tokens.find( t => ICONS[ t ] || ICONS[ 'brand:' + t ] ) || tokens[ 0 ] || '';
    }

    this.def =
      ( isBrand ? ICONS[ 'brand:' + nm ] : null ) ||
      ICONS[ nm ] ||
      ICONS[ 'brand:' + nm ] ||
      null;
    this.paths = this.def
      ? ( Array.isArray( this.def.p ) ? this.def.p : [ this.def.p ] )
      : [];
  }
}
